import { Button, Divider, Form, Row, Spin } from "antd";
import Search from "antd/lib/transfer/search";
import {
  generateReports,
  ResponseEntryDetail,
  traspasosPage,
} from "api/generated";
import { FormItem } from "common/components/form/FormItem";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import { querySerializer } from "common/helpers/queryUtils";
import { useDateRange } from "context/DateRangeContext";
import DistributionPointSelected from "context/MyDistributionPoint";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { useTableSelection } from "hooks/commons/useTableSelection";
import { useClient } from "hooks/useClient";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { saveJsonToFile } from "routes/stockControl/components/jsonViewer";
import TraspasosTable from "./components/TraspasosTable";
import { COMPLEMENT_PARENT } from "routes/cfdiComplements/ComplementsScreen";
import { handleAxiosError } from "common/helpers/generalUtils";
import {
  EXPORT_REPORT_PERMISSION,
  getPagePermissions,
} from "security/permissions";
import useLoggedUser from "hooks/useLoggedUser";
import { PlusOutlined } from "@ant-design/icons";
import CreateTraspaso from "./components/createTraspaso";
import ComplementOptions from "common/components/complements/ComplementOptions";

function TraspasosScreen(props: RouteComponentProps) {
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const [isLoading, setIsLoading] = useState(false);
  const { permissions, providerDataOrigin } = useLoggedUser();
  const exportReportsPermissions = getPagePermissions(
    EXPORT_REPORT_PERMISSION,
    permissions,
  );

  const selection = useTableSelection();
  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates();
  const pagination = usePaginationTable<ResponseEntryDetail>(props, {
    initialSort: ["folio,DESC"],
    persistInUrl: true,
  });

  const [parentIdToDelete, setParentIdToDelete] = useState<string>();
  const [traspaso, setTraspaso] = useState<ResponseEntryDetail>();
  const [showUpsert, setShowUpsert] = useState(false);

  const query = useMemo(() => {
    const value = pagination.debouncedFilterText;
    if (value.length >= 1 && !isNaN(Number(value))) {
      return pagination.debouncedFilterText;
    }
    return undefined;
  }, [pagination.debouncedFilterText]);

  function downloadReporte() {
    if (distributionPoint) {
      setIsLoading(true);
      generateReports({
        client,
        body: {
          inputRegisterStartDate: startDate,
          inputRegisterEndDate: endDate,
          query,
          distributionPointId: distributionPoint?.id,
        },
        path: {
          type: "csv",
        },
      })
        .then(({ data }) => {
          saveJsonToFile(data, "text/csv", "traspasos.csv");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  const reloadTable = useCallback(async () => {
    if (!distributionPoint) {
      return;
    }
    setIsLoading(true);
    traspasosPage({
      client,
      querySerializer: querySerializer,
      query: {
        pageable: {
          page: pagination.currentPage,
          size: pagination.pageSize,
          sort: pagination.sort,
        },
      },
      body: {
        inputRegisterStartDate: startDate,
        inputRegisterEndDate: endDate,
        query,
        distributionPointId: distributionPoint.id,
      },
      path: {
        distributionPointId: distributionPoint.id,
      },
    })
      .then(({ data }) => {
        pagination.setTotalItems(data?.totalElements ?? 0);
        pagination.setItems(data?.content ?? []);
      })
      .catch((err) => handleAxiosError(err, "No fue posible obtener traspasos"))
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    client,
    startDate,
    endDate,
    query,
    distributionPoint,
    pagination.currentPage,
    pagination.pageSize,
  ]);

  // useEffect principal
  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  // Reset cuando cambian filtros principales
  useEffect(() => {
    if (props.location.search && props.location.search.length > 0) {
      return;
    }
  }, [distributionPoint?.id, startDate, endDate]);

  useEffect(() => {
    selection.resetSelected();
  }, [distributionPoint?.id, startDate, endDate]);

  return (
    <Spin size="large" tip="Cargando..." spinning={isLoading}>
      <Form layout="vertical">
        <Row gutter={16} align="bottom">
          <ResponsiveCol>
            <FormItem label="Seleccionar periodo">
              <DateRangePicker
                config={{
                  allowedTypes: [
                    DateFilterType.DAY,
                    DateFilterType.MONTH,
                    DateFilterType.RANGE,
                  ],
                  defaultType: DateFilterType.MONTH,
                  showQuickFilters: true,
                }}
                rangeTimeValue={rangeTimeValue}
                setRangeTimeValue={setRangeTimeValue}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem label="Búsqueda por número de registro (Folio)">
              <Search
                onChange={(event) => {
                  pagination.setFilterText(event.target.value);
                }}
                value={pagination.filterText}
              />
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol>
            <FormItem>
              <Button
                style={{ width: "100%" }}
                onClick={downloadReporte}
                disabled={
                  !exportReportsPermissions || pagination.totalItems === 0
                }
              >
                Descargar CSV
              </Button>
            </FormItem>
          </ResponsiveCol>

          {providerDataOrigin !== "EXTERNAL" && (
            <ResponsiveCol size="xs">
              <FormItem>
                <Button
                  style={{ width: "100%" }}
                  onClick={() => {
                    setShowUpsert(true);
                    setTraspaso(undefined);
                  }}
                  type="primary"
                  icon={<PlusOutlined />}
                >
                  Registrar un traspaso
                </Button>
              </FormItem>
            </ResponsiveCol>
          )}
        </Row>
      </Form>

      <ComplementOptions
        selection={selection}
        reloadTable={reloadTable}
        parentType={COMPLEMENT_PARENT.TRASPASO}
        parentIdToDelete={parentIdToDelete}
        clearParentIdToDelete={() => {
          setParentIdToDelete(undefined);
        }}
      />

      <TraspasosTable
        selection={selection}
        isLoading={isLoading}
        reloadTable={reloadTable}
        pagination={pagination}
        deleteComplement={(record) => {
          setParentIdToDelete(record.id);
        }}
        editTraspaso={(record) => {
          setTraspaso(record);
          setShowUpsert(true);
        }}
        location={props.location}
        history={props.history}
        match={props.match}
      />

      {showUpsert && (
        <CreateTraspaso
          hidden={!showUpsert}
          closeModal={() => {
            setTraspaso(undefined);
            setShowUpsert(false);
          }}
          distributionPointId={distributionPoint?.id ?? ""}
          refreshView={() => {
            reloadTable();
          }}
          traspaso={traspaso}
        />
      )}
      <Divider />
    </Spin>
  );
}

export default withRouter(TraspasosScreen);
